import React from 'react';
import { CalendarDays, Clock, Heart, Star, ThumbsUp, Upload } from 'lucide-react';
import {
  ICONOS_CALIFICACION, MAX_ARCHIVOS, MAX_MB, TIPOS_ARCHIVO,
} from '../../../lib/formularios';
import { useEditor } from './contexto';
import { FilaInterruptor, GlifoOpcion, ImagenAdjunta, Selector } from './controles';
import EditorOpciones from './EditorOpciones';
import EditorCuadricula from './EditorCuadricula';

export const ICONO_CALIFICACION = { estrella: Star, corazon: Heart, pulgar: ThumbsUp };

/**
 * Cuerpo de una pregunta. `editable`: la tarjeta activa (se configuran opciones, escala, archivos…).
 * Sin `editable`: la vista de la tarjeta inactiva, parecida a lo que ve quien responde pero sin poder
 * contestar — así el formulario se lee de un vistazo sin abrir cada pregunta.
 */
export default function CuerpoPregunta({ item, editable }) {
  switch (item.tipo) {
    case 'texto_corto':
      return <div className="fe-muestra-texto">Texto de respuesta breve</div>;
    case 'parrafo':
      return <div className="fe-muestra-texto fe-muestra-larga">Texto de respuesta larga</div>;
    case 'opcion_multiple':
    case 'casillas':
    case 'desplegable':
      return editable ? <EditorOpciones item={item} /> : <MuestraOpciones item={item} />;
    case 'archivo':
      return editable ? <ConfigArchivo item={item} /> : <MuestraArchivo item={item} />;
    case 'escala':
      return editable ? <ConfigEscala item={item} /> : <MuestraEscala item={item} />;
    case 'calificacion':
      return editable ? <ConfigCalificacion item={item} /> : <MuestraCalificacion item={item} />;
    case 'cuadricula_opcion':
    case 'cuadricula_casillas':
      return editable ? <EditorCuadricula item={item} /> : <MuestraCuadricula item={item} />;
    case 'fecha':
      return (
        <div className="fe-muestra-campo">
          <span>{item.fecha?.incluir_anio === false ? 'Mes, día' : 'Día, mes, año'}</span>
          <CalendarDays />
          {item.fecha?.incluir_hora && (
            <>
              <span className="fe-muestra-sep" />
              <span>Hora</span>
              <Clock />
            </>
          )}
        </div>
      );
    case 'hora':
      return (
        <div className="fe-muestra-campo">
          <span>{item.hora?.duracion ? 'Duración (horas, minutos, segundos)' : 'Hora'}</span>
          <Clock />
        </div>
      );
    default:
      return null;
  }
}

function MuestraOpciones({ item }) {
  const opciones = item.opciones || [];
  return (
    <div className="fe-muestra-opciones">
      {opciones.map((o, i) => (
        <div key={o.id} className="fe-muestra-opcion">
          <div className="fe-opcion-fila">
            <GlifoOpcion tipo={item.tipo} indice={i} />
            <span className="fe-muestra-opcion-texto">{o.texto}</span>
          </div>
          <ImagenAdjunta imagen={o.imagen} alt={o.texto} className="fe-imagen-opcion" />
        </div>
      ))}
      {item.otra && item.tipo !== 'desplegable' && (
        <div className="fe-opcion-fila">
          <GlifoOpcion tipo={item.tipo} indice={opciones.length} />
          <span className="fe-muestra-opcion-texto">Otro: <span className="fe-muestra-linea" /></span>
        </div>
      )}
    </div>
  );
}

function ConfigArchivo({ item }) {
  const { cambiarItem } = useEditor();
  const cfg = item.archivo || { tipos: [], max_archivos: 1, max_mb: 10 };
  const set = (campos) => cambiarItem(item.id, { archivo: { ...cfg, ...campos } });
  const soloTipos = (cfg.tipos || []).length > 0;
  return (
    <div className="fe-config-archivo">
      <FilaInterruptor
        id={`tipos-${item.id}`}
        titulo="Permitir solo tipos de archivo específicos"
        checked={soloTipos}
        onChange={(v) => set({ tipos: v ? ['documento', 'pdf', 'imagen'] : [] })}
      />
      {soloTipos && (
        <div className="fe-tipos-archivo">
          {Object.entries(TIPOS_ARCHIVO).map(([clave, t]) => (
            <label key={clave} className="fe-check">
              <input
                type="checkbox"
                checked={cfg.tipos.includes(clave)}
                onChange={(e) => {
                  const tipos = e.target.checked ? [...cfg.tipos, clave] : cfg.tipos.filter((x) => x !== clave);
                  set({ tipos: tipos.length ? tipos : [] });
                }}
              />
              {t.nombre}
            </label>
          ))}
        </div>
      )}
      <div className="fe-fila-config">
        <span>Número máximo de archivos</span>
        <Selector
          value={cfg.max_archivos}
          onChange={(v) => set({ max_archivos: Number(v) })}
          opciones={MAX_ARCHIVOS.map((n) => ({ valor: n, texto: String(n) }))}
          etiqueta="Número máximo de archivos"
        />
      </div>
      <div className="fe-fila-config">
        <span>Tamaño máximo por archivo</span>
        <Selector
          value={cfg.max_mb}
          onChange={(v) => set({ max_mb: Number(v) })}
          opciones={MAX_MB.map((n) => ({ valor: n, texto: n >= 1000 ? `${n / 1000} GB` : `${n} MB` }))}
          etiqueta="Tamaño máximo por archivo"
        />
      </div>
      <p className="fe-nota">
        Los archivos se guardan en tu Drive, en la carpeta «{'<título>'} (archivos de respuestas)», y cuentan para el
        espacio de la organización. Cualquiera con el enlace podrá subir archivos si el formulario no pide iniciar sesión.
      </p>
    </div>
  );
}

function MuestraArchivo({ item }) {
  const cfg = item.archivo || {};
  return (
    <div className="fe-muestra-archivo">
      <span className="fe-boton-muestra"><Upload /> Añadir archivo</span>
      <span className="fe-texto-2">
        Máximo {cfg.max_archivos || 1} {cfg.max_archivos === 1 ? 'archivo' : 'archivos'} · {cfg.max_mb || 10} MB cada uno
      </span>
    </div>
  );
}

function ConfigEscala({ item }) {
  const { cambiarItem } = useEditor();
  const e = item.escala || { min: 1, max: 5, etiqueta_min: '', etiqueta_max: '' };
  const set = (campos, clave) => cambiarItem(item.id, { escala: { ...e, ...campos } }, { clave });
  return (
    <div className="fe-config-escala">
      <div className="fe-fila-config fe-fila-rango">
        <Selector value={e.min} onChange={(v) => set({ min: Number(v) })} opciones={[0, 1].map((n) => ({ valor: n, texto: String(n) }))} etiqueta="Desde" />
        <span>a</span>
        <Selector
          value={e.max}
          onChange={(v) => set({ max: Number(v) })}
          opciones={[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ valor: n, texto: String(n) }))}
          etiqueta="Hasta"
        />
      </div>
      <div className="fe-fila-etiqueta">
        <span className="fe-texto-2">{e.min}</span>
        <input
          className="fm-campo-linea"
          value={e.etiqueta_min || ''}
          placeholder="Etiqueta (opcional)"
          aria-label={`Etiqueta de ${e.min}`}
          onChange={(ev) => set({ etiqueta_min: ev.target.value }, `${item.id}:escala:min`)}
        />
      </div>
      <div className="fe-fila-etiqueta">
        <span className="fe-texto-2">{e.max}</span>
        <input
          className="fm-campo-linea"
          value={e.etiqueta_max || ''}
          placeholder="Etiqueta (opcional)"
          aria-label={`Etiqueta de ${e.max}`}
          onChange={(ev) => set({ etiqueta_max: ev.target.value }, `${item.id}:escala:max`)}
        />
      </div>
    </div>
  );
}

function MuestraEscala({ item }) {
  const e = item.escala || { min: 1, max: 5 };
  const valores = [];
  for (let n = e.min; n <= e.max; n += 1) valores.push(n);
  return (
    <div className="fe-muestra-escala">
      {e.etiqueta_min && <span className="fe-escala-etiqueta">{e.etiqueta_min}</span>}
      <div className="fe-escala-valores">
        {valores.map((n) => (
          <span key={n} className="fe-escala-valor">
            <span className="fe-texto-2">{n}</span>
            <span className="fe-glifo fe-glifo-radio" />
          </span>
        ))}
      </div>
      {e.etiqueta_max && <span className="fe-escala-etiqueta">{e.etiqueta_max}</span>}
    </div>
  );
}

function ConfigCalificacion({ item }) {
  const { cambiarItem } = useEditor();
  const c = item.calificacion || { max: 5, icono: 'estrella' };
  const set = (campos) => cambiarItem(item.id, { calificacion: { ...c, ...campos } });
  return (
    <div className="fe-config-escala">
      <div className="fe-fila-config fe-fila-rango">
        <span>Niveles</span>
        <Selector
          value={c.max}
          onChange={(v) => set({ max: Number(v) })}
          opciones={[3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ valor: n, texto: String(n) }))}
          etiqueta="Número de niveles"
        />
        <span>Símbolo</span>
        <Selector
          value={c.icono}
          onChange={(v) => set({ icono: v })}
          opciones={Object.entries(ICONOS_CALIFICACION).map(([valor, texto]) => ({ valor, texto }))}
          etiqueta="Símbolo"
        />
      </div>
      <MuestraCalificacion item={item} />
    </div>
  );
}

function MuestraCalificacion({ item }) {
  const c = item.calificacion || { max: 5, icono: 'estrella' };
  const Icono = ICONO_CALIFICACION[c.icono] || Star;
  return (
    <div className="fe-muestra-calificacion" aria-hidden="true">
      {Array.from({ length: c.max }, (_, i) => (
        <span key={i} className="fe-calificacion-nivel">
          <span className="fe-texto-2">{i + 1}</span>
          <Icono />
        </span>
      ))}
    </div>
  );
}

function MuestraCuadricula({ item }) {
  const filas = item.filas || [];
  const columnas = item.columnas || [];
  const glifo = item.tipo === 'cuadricula_casillas' ? 'casillas' : 'opcion_multiple';
  return (
    <div className="fe-muestra-cuadricula fm-scroll">
      <table>
        <thead>
          <tr>
            <th aria-hidden="true" />
            {columnas.map((c) => <th key={c.id}>{c.texto}</th>)}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.id}>
              <th scope="row">{f.texto}</th>
              {columnas.map((c, i) => (
                <td key={c.id}><GlifoOpcion tipo={glifo} indice={i} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

